import { useCallback, useEffect, useRef, useState } from 'react';

interface EncodedAudio {
  audioBase64: string;
  mimeType: string;
}

export function usePreloadedAudio(
  sourceKey: string | null,
  loadSource: (sourceKey: string) => Promise<EncodedAudio>
) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;

    audioRef.current?.pause();
    audioRef.current = null;
    setReady(false);
    setPlaying(false);
    setLoadFailed(false);

    if (!sourceKey) return;

    void loadSource(sourceKey)
      .then(source => {
        const binary = atob(source.audioBase64);
        const bytes = Uint8Array.from(binary, character => character.charCodeAt(0));
        objectUrl = URL.createObjectURL(new Blob([bytes], { type: source.mimeType }));

        if (!active) {
          URL.revokeObjectURL(objectUrl);
          return;
        }

        const player = new Audio(objectUrl);
        player.preload = 'auto';
        player.onended = () => {
          if (active) setPlaying(false);
        };
        audioRef.current = player;
        setReady(true);
      })
      .catch(() => {
        if (active) setLoadFailed(true);
      });

    return () => {
      active = false;
      audioRef.current?.pause();
      audioRef.current = null;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [loadSource, sourceKey]);

  const play = useCallback(async () => {
    const player = audioRef.current;
    if (!player) throw new Error('Audio is not ready');

    player.currentTime = 0;
    setPlaying(true);
    try {
      await player.play();
    } catch (error) {
      setPlaying(false);
      throw error;
    }
  }, []);

  return { loadFailed, play, playing, ready };
}
